/* Minimal Maskan — interactive behaviour for custom sections.
   Builds on Dawn's globals (routes, fetchConfig, cart-notification / cart-drawer). */
(function () {
  const MM = (window.MM = window.MM || {});

  /* Matches the store's "$24.99 CAD" money-with-currency format. */
  MM.money = function (cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
    const amount = (Number(cents) / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '$' + amount + ' ' + currency;
  };

  /* Add one or more variants and open Dawn's bag popover / drawer. */
  MM.addItems = async function (items) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = { items: items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.status) {
      window.alert(data.description || data.message);
      return null;
    }
    if (!cart) {
      window.location = window.routes.cart_url;
      return data;
    }
    const first = data.items[data.items.length - 1];
    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
    cart.renderContents(Object.assign({}, first, { sections: data.sections, key: first.key, id: first.id }));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mm', productVariantId: first.variant_id, cartData: data });
    }
    return data;
  };

  function busy(btn, on) {
    if (!btn) return;
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.disabled = !!on;
  }

  /* Any [data-mm-add="variantId,variantId"] button adds those variants. */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-mm-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const ids = btn.getAttribute('data-mm-add').split(',').filter(Boolean);
    if (!ids.length) return;
    busy(btn, true);
    try {
      await MM.addItems(ids.map((id) => ({ id: Number(id), quantity: 1 })));
    } finally {
      busy(btn, false);
    }
  });

  /* ---------- Tabs ---------- */
  class MMTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }
  }
  customElements.define('mm-tabs', MMTabs);

  /* ---------- Shop the look ---------- */
  class MMLook extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-look-index]').forEach((el) => {
        el.addEventListener('click', (e) => {
          if (e.target.closest('[data-mm-add]')) return;
          this.select(el.getAttribute('data-look-index'));
        });
      });
    }
    select(index) {
      this.querySelectorAll('[data-look-index]').forEach((el) => {
        el.classList.toggle('is-active', el.getAttribute('data-look-index') === String(index));
      });
    }
  }
  customElements.define('mm-look', MMLook);

  /* ---------- Announcement bar: rotating messages ----------
     Advances on a fixed timer (no hover pause). Holds only while a message link has
     keyboard focus, so focus is never left on a hidden message. */
  class MMAnnounce extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.mm-announce__slide'));
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = Number(this.dataset.interval) || 5000;
      this.focused = false;
      this.addEventListener('focusin', (e) => e.target.matches(':focus-visible') && this.hold(true));
      this.addEventListener('focusout', (e) => !this.contains(e.relatedTarget) && this.hold(false));
      this.restart();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    go(index) {
      const previous = this.index;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const on = i === this.index;
        slide.classList.toggle('is-leaving', i === previous && !on);
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
        slide.querySelectorAll('a').forEach((a) => (on ? a.removeAttribute('tabindex') : (a.tabIndex = -1)));
      });
    }
    hold(on) {
      this.focused = on;
      this.restart();
    }
    restart() {
      clearInterval(this.timer);
      if (!this.focused) this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }
  }
  customElements.define('mm-announce', MMAnnounce);

  /* ---------- Bundle / frequently bought together ---------- */
  class MMBundle extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-bundle-item]'));
      this.min = Number(this.dataset.min || 0);
      this.pct = Number(this.dataset.discount || 0);
      this.rows.forEach((row) => row.addEventListener('click', () => this.toggle(row)));
      this.update();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && this.querySelector('[data-bundle-current]')) {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (!variant) return;
          const row = this.querySelector('[data-bundle-item][data-bundle-current]');
          if (!row) return;
          row.dataset.variant = variant.id;
          row.dataset.price = variant.price;
          const title = row.querySelector('[data-bundle-variant-title]');
          if (title) title.textContent = variant.title;
          const price = row.querySelector('[data-bundle-price]');
          if (price) price.textContent = MM.money(variant.price);
          const thumb = this.querySelector('img[data-bundle-current]');
          if (thumb) thumb.dataset.variant = variant.id;
          const unavailable = variant.available === false;
          row.disabled = unavailable;
          if (unavailable && row.getAttribute('aria-checked') === 'true') this.toggle(row);
          else this.update();
        });
      }
    }
    disconnectedCallback() {
      if (this.unsub) this.unsub();
    }
    toggle(row) {
      const on = row.getAttribute('aria-checked') !== 'true';
      row.setAttribute('aria-checked', on ? 'true' : 'false');
      row.classList.toggle('is-selected', on);
      this.update();
    }
    update() {
      const chosen = this.rows.filter((r) => r.getAttribute('aria-checked') === 'true');
      const sum = chosen.reduce((a, r) => a + Number(r.dataset.price || 0), 0);
      const unlocked = this.pct > 0 && chosen.length >= this.min;
      const total = unlocked ? Math.round(sum * (1 - this.pct / 100)) : sum;
      this.querySelectorAll('[data-bundle-total]').forEach((el) => (el.textContent = MM.money(total)));
      this.querySelectorAll('[data-bundle-count]').forEach((el) => (el.textContent = chosen.length));
      this.querySelectorAll('[data-bundle-thumb]').forEach((img) => {
        const row = this.rows.find((r) => r.dataset.variant === img.dataset.variant);
        img.classList.toggle('is-off', !row || row.getAttribute('aria-checked') !== 'true');
      });
      const hint = this.querySelector('[data-bundle-hint]');
      if (hint && this.pct > 0) {
        if (unlocked) {
          hint.textContent = (hint.dataset.saving || 'You’re saving [amount] with the set').replace('[amount]', MM.money(sum - total));
          hint.style.color = 'var(--mm-success)';
        } else {
          hint.textContent = (hint.dataset.more || 'Add [count] more to unlock [pct]% off')
            .replace('[count]', this.min - chosen.length)
            .replace('[pct]', this.pct);
          hint.style.color = '';
        }
      }
      const add = this.querySelector('[data-bundle-add]');
      if (add) {
        add.setAttribute('data-mm-add', chosen.map((r) => r.dataset.variant).join(','));
        add.disabled = chosen.length === 0;
      }
    }
  }
  customElements.define('mm-bundle', MMBundle);

  /* ---------- VIP Circle pop-up ---------- */
  class MMVip extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.key = 'mm-vip-dismissed';
      this.querySelectorAll('[data-vip-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('cancel', () => this.remember());
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('a[href="#vip"], [data-vip-open]');
        if (!trigger) return;
        e.preventDefault();
        this.open();
      });
      if (this.dataset.posted === 'true') {
        this.open();
        return;
      }
      const delay = Number(this.dataset.delay || 0);
      if (this.dataset.auto === 'true' && !this.dismissed() && !window.Shopify.designMode) {
        // Don't auto-open over another open panel (e.g. the VIP exclusive panel).
        setTimeout(() => {
          if (!document.querySelector('dialog[open]')) this.open();
        }, delay * 1000);
      }
    }
    dismissed() {
      try {
        return window.localStorage.getItem(this.key) === '1';
      } catch (e) {
        return false;
      }
    }
    remember() {
      try {
        window.localStorage.setItem(this.key, '1');
      } catch (e) {}
    }
    open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
    }
    close() {
      this.remember();
      this.dialog.close();
      document.body.classList.remove('overflow-hidden');
    }
  }
  customElements.define('mm-vip', MMVip);

  /* ---------- VIP Circle exclusive panel (opens from product cards) ---------- */
  class MMVipGateModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.querySelectorAll('[data-gate-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
        // "Join the VIP Circle" hands over to the VIP sign-up pop-up.
        if (e.target.closest('[data-vip-open]')) this.close();
      });
      this.dialog.addEventListener('close', () => document.body.classList.remove('overflow-hidden'));
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-vip-gate-open]');
        if (!trigger) return;
        e.preventDefault();
        this.open(trigger);
      });
    }
    open(trigger) {
      const d = trigger.dataset;
      const set = (sel, value) => {
        const el = this.querySelector(sel);
        if (el) el.textContent = value || '';
      };
      set('[data-gate-title]', d.title);
      const img = this.querySelector('[data-gate-image]');
      if (img) {
        img.hidden = !d.image;
        if (d.image) {
          img.src = d.image;
          img.alt = d.title || '';
        }
      }
      const link = this.querySelector('[data-gate-url]');
      if (link) link.href = d.url || '#';
      this.returnFocus = trigger;
      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
    }
    close() {
      if (this.dialog.open) this.dialog.close();
      document.body.classList.remove('overflow-hidden');
      if (this.returnFocus) this.returnFocus.focus({ preventScroll: true });
    }
  }
  customElements.define('mm-vip-gate-modal', MMVipGateModal);

  /* ---------- FAQ: live search, topic nav ---------- */
  class MMFaq extends HTMLElement {
    connectedCallback() {
      this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
      this.empty = this.querySelector('[data-faq-empty]');
      this.topic = 'all';
      this.query = '';
      this.querySelectorAll('[data-faq-topic]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.topic = btn.dataset.faqTopic;
          this.querySelectorAll('[data-faq-topic]').forEach((b) =>
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false')
          );
          this.filter();
        })
      );
      const own = this.querySelector('[data-faq-search]');
      if (own) own.addEventListener('input', (e) => this.setQuery(e.target.value));
      document.addEventListener('mm:faq-search', (e) => this.setQuery(e.detail));
    }
    setQuery(q) {
      this.query = (q || '').trim().toLowerCase();
      this.filter();
    }
    filter() {
      let shown = 0;
      this.groups.forEach((g) => {
        const topicOk = this.topic === 'all' || g.dataset.faqGroup === this.topic;
        let visible = 0;
        g.querySelectorAll('[data-faq-item]').forEach((item) => {
          const ok = !this.query || item.textContent.toLowerCase().includes(this.query);
          item.hidden = !ok;
          if (ok) visible++;
          if (this.query && ok) item.setAttribute('open', '');
        });
        g.hidden = !topicOk || visible === 0;
        if (!g.hidden) shown++;
      });
      if (this.empty) {
        this.empty.hidden = shown > 0;
        const q = this.empty.querySelector('[data-faq-query]');
        if (q) q.textContent = this.query;
      }
    }
  }
  customElements.define('mm-faq', MMFaq);

  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-faq-search-global]')) return;
    document.dispatchEvent(new CustomEvent('mm:faq-search', { detail: e.target.value }));
  });

  /* ---------- Recently viewed ---------- */
  const RV_KEY = 'mm-recently-viewed';
  function readRecent() {
    try {
      return JSON.parse(window.localStorage.getItem(RV_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }
  const current = document.body && document.body.dataset.mmProduct;
  if (current) {
    const list = [current].concat(readRecent().filter((h) => h !== current)).slice(0, 12);
    try {
      window.localStorage.setItem(RV_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  class MMRecent extends HTMLElement {
    async connectedCallback() {
      const limit = Number(this.dataset.limit || 6);
      const handles = readRecent().filter((h) => h !== current).slice(0, limit);
      const list = this.querySelector('[data-recent-list]');
      if (!handles.length || !list) {
        this.hidden = true;
        return;
      }
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const products = await Promise.all(
        handles.map((h) =>
          fetch(root + 'products/' + h + '.js')
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      );
      const html = products
        .filter(Boolean)
        .map((p) => {
          const img = p.featured_image
            ? '<img src="' + p.featured_image + (p.featured_image.includes('?') ? '&' : '?') + 'width=160" alt="" loading="lazy" width="64" height="64">'
            : '<span class="mm-mini__ph"></span>';
          const title = document.createElement('span');
          title.textContent = p.title;
          return (
            '<a class="mm-mini" href="' + p.url + '">' + img + '<span><span style="display:block">' + title.innerHTML +
            '</span><span class="mm-muted">' + MM.money(p.price) + '</span></span></a>'
          );
        })
        .join('');
      if (!html) {
        this.hidden = true;
        return;
      }
      list.innerHTML = html;
      this.hidden = false;
      const clear = this.querySelector('[data-recent-clear]');
      if (clear)
        clear.addEventListener('click', () => {
          try {
            window.localStorage.removeItem(RV_KEY);
          } catch (e) {}
          this.hidden = true;
        });
    }
  }
  customElements.define('mm-recent', MMRecent);

  /* ---------- Sticky mobile add bar ---------- */
  class MMStickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector(this.dataset.watch || 'product-form');
      if (!target || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(target);
      const btn = this.querySelector('button');
      if (btn)
        btn.addEventListener('click', () => {
          const submit = target.querySelector('[type="submit"][name="add"]');
          if (submit) submit.click();
        });
    }
  }
  customElements.define('mm-sticky-atc', MMStickyAtc);

  /* ---------- "Complete the room" recommendations in the bag popover ---------- */
  function loadUpsell(productId) {
    const box = document.getElementById('mm-cart-upsell');
    if (!box || !productId) return;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'recommendations/products?section_id=mm-cart-upsell&limit=2&intent=complementary&product_id=' + productId)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('.mm-upsell');
        if (inner && inner.querySelector('.mm-upsell__item')) {
          box.innerHTML = inner.innerHTML;
          return;
        }
        return fetch(root + 'recommendations/products?section_id=mm-cart-upsell&limit=2&product_id=' + productId)
          .then((r) => r.text())
          .then((html2) => {
            const d2 = new DOMParser().parseFromString(html2, 'text/html').querySelector('.mm-upsell');
            box.innerHTML = d2 ? d2.innerHTML : '';
          });
      })
      .catch(() => {
        box.innerHTML = '';
      });
  }

  /* ---------- Header dropdowns: open on hover with a mouse ----------
     Touch and pen keep Dawn's tap-to-open; keyboard keeps Enter/Space. */
  const hoverMenu = { timer: null, last: null, pointer: '' };
  document.addEventListener('pointerdown', (event) => (hoverMenu.pointer = event.pointerType), true);
  function setMenuOpen(menu, open) {
    const details = menu && menu.querySelector('details');
    if (!details || details.open === open) return;
    details.open = open;
    details.querySelector('summary').setAttribute('aria-expanded', open);
  }
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const menu = event.target.closest('.header__inline-menu header-menu');
    if (!menu) return;
    clearTimeout(hoverMenu.timer);
    if (hoverMenu.last && hoverMenu.last !== menu) setMenuOpen(hoverMenu.last, false);
    hoverMenu.last = menu;
    setMenuOpen(menu, true);
  });
  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return;
    const menu = event.target.closest('.header__inline-menu header-menu');
    if (!menu || menu.contains(event.relatedTarget)) return;
    clearTimeout(hoverMenu.timer);
    hoverMenu.timer = setTimeout(() => setMenuOpen(menu, false), 200);
  });
  /* A mouse click on a hover-opened title would otherwise close it again. */
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('.header__inline-menu header-menu summary');
    if (summary && event.detail > 0 && hoverMenu.pointer === 'mouse' && summary.parentElement.open) event.preventDefault();
  });

  /* ---------- Gentle reveal: sections below the fold fade up once ----------
     Skips sections Dawn already animates and anything visible on load. */
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const revealer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    document.querySelectorAll('main .shopify-section').forEach((section) => {
      if (section.querySelector('.scroll-trigger') || section.getBoundingClientRect().top < window.innerHeight) return;
      section.classList.add('mm-reveal');
      revealer.observe(section);
    });
    document.addEventListener('shopify:section:load', (event) => event.target.classList.add('is-revealed'));
  }

  customElements.whenDefined('cart-notification').then(() => {
    const Ctor = customElements.get('cart-notification');
    const original = Ctor.prototype.renderContents;
    Ctor.prototype.renderContents = function (state) {
      original.call(this, state);
      loadUpsell(state && state.product_id);
    };
  });
})();
